<template>
  <div v-if="fetchingCustomization">
    <LoadingSkeletonRow :possible-widths="['85%', '90%', '88%']" height="16px" />
    <LoadingSkeletonRow :possible-widths="['82%', '87%', '85%']" height="16px" />
    <LoadingSkeletonRow :possible-widths="['88%', '84%', '90%']" height="16px" />
    <LoadingSkeletonRow :possible-widths="['80%', '86%', '83%']" height="16px" />
  </div>
  <tabs
    v-else
    justified
    v-model="tabIndex"
    :transition="0"
    @change="clickedTab">
    <tab
      v-for="(obj, index) in objectives"
      :key="index"
      :title="obj.level + ' Quarter'"
      :tab-classes="tabLevelClasses(obj)"
    >
      <Objective
        :level="obj.level"
        :title="obj.title"
        :text="levelledObjectiveTexts[index]"
        :items="items && items[obj.level]"
        :access-info="accessInfo"
        :print-permissions="printPermissions"
        :page-uuid="pageUuid"
      >
        <template slot="body">
          <EditableWikiTextArea
            v-if="editable && levelledObjectiveTexts[index]"
            v-model="levelledObjectiveTexts[index]"
            :strategies="strategyMap"
            @textEdited="objectiveTextEdited(index, obj)"
            @textReset="objectiveTextEdited(index, obj)"
          />
          <WikiText
            v-if="!editable && obj.text"
            :text="obj.text"
            :strategies="strategyMap"
            :locked="accessInfo.blur"
          />
        </template>
        <WikiText
          slot="objective"
          :text="levelledObjectiveTexts[index] || obj.text"
          :strategies="strategyMap"
          popover-placement="bottom"
          :locked="accessInfo.blur"
        />
        <template
          slot="footer"
          v-if="
            resourceUuids[indexToQuarter[index]] && resourceUuids[indexToQuarter[index]].length > 0
          "
        >
          <template>
            <template v-if="loadingResources">
              <p class="loading-resources gb-text-2">
                <i class="fa fa-circle-o-notch fa-spin" /> Loading Resources...
              </p>
            </template>
            <div class="resources-container" v-if="!loadingResources">
              <template v-if="objectiveResources[indexToQuarter[index]].assessments.length > 0">
                <h4 class="resources-header">Assessments</h4>
                <ResourcePreviewCard
                  v-for="resource in objectiveResources[indexToQuarter[index]].assessments"
                  :key="resource.uuid"
                  v-bind="resource"
                />
              </template>
              <template v-if="objectiveResources[indexToQuarter[index]].resources.length > 0">
                <h4 class="resources-header">Ready-To-Use Resources</h4>
                <ResourcePreviewCard
                  v-for="resource in objectiveResources[indexToQuarter[index]].resources"
                  :key="resource.uuid"
                  v-bind="resource"
                />
              </template>
            </div>
          </template>
        </template>
      </Objective>
    </tab>
  </tabs>
</template>

<script>
import { mapGetters } from 'vuex'
import EditableWikiTextArea from '@/components/EditableWikiTextArea'
import WikiText from '@/components/WikiText'
import Objective from '@/components/goals/Objective'
import ResourcePreviewCard from '@/components/ResourcePreviewCard'
import LoadingSkeletonRow from '@/components/LoadingSkeletonRow'
import { getResources } from '@/api/pathways'
import { ObjectiveLevelValues } from '@/components/goals/common/objectives'
function emptyResourcesMap() {
  return {
    [ObjectiveLevelValues.QUARTER_1ST]: {
      assessments: [],
      resources: [],
    },
    [ObjectiveLevelValues.QUARTER_2ND]: {
      assessments: [],
      resources: [],
    },
    [ObjectiveLevelValues.QUARTER_3RD]: {
      assessments: [],
      resources: [],
    },
  }
}

export default {
  name: 'AnchorPageObjectives',
  components: {
    EditableWikiTextArea,
    WikiText,
    Objective,
    ResourcePreviewCard,
    LoadingSkeletonRow,
  },
  props: {
    objectives: {
      type: Array,
      default() {
        return []
      },
    },
    items: {
      type: Object,
      default() {
        return {}
      },
    },
    // this prop is basically just for inclusion in analytics...
    pageUuid: {
      type: String,
      default: '',
    },
    accessInfo: {
      type: Object,
      required: true,
    },
    printPermissions: {
      type: Object,
      required: true,
    },
    fetchingCustomization: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    levelledObjectiveTexts: {
      type: Array,
      default() {
        return []
      },
    },
    objectiveResourceUuids: {
      type: Object,
      default() {
        return {
          [ObjectiveLevelValues.QUARTER_1ST]: [],
          [ObjectiveLevelValues.QUARTER_2ND]: [],
          [ObjectiveLevelValues.QUARTER_3RD]: [],
        }
      },
    },
  },
  data() {
    return {
      tabIndex: 0,
      objectiveResources: emptyResourcesMap(),
      indexToQuarter: [
        ObjectiveLevelValues.QUARTER_1ST,
        ObjectiveLevelValues.QUARTER_2ND,
        ObjectiveLevelValues.QUARTER_3RD,
      ],
      loadingResources: false,
    }
  },
  computed: {
    ...mapGetters('goaldetail', ['strategyMap']),
    resourceUuids() {
      var uuids = {
        [ObjectiveLevelValues.QUARTER_1ST]: [],
        [ObjectiveLevelValues.QUARTER_2ND]: [],
        [ObjectiveLevelValues.QUARTER_3RD]: [],
      }
      this.indexToQuarter.forEach((quarter) => {
        if (this.objectiveResourceUuids && !!this.objectiveResourceUuids[quarter]) {
          uuids[quarter] = this.objectiveResourceUuids[quarter]
        }
      })
      return uuids
    },
  },
  methods: {
    objectiveTextEdited(index) {
      this.$emit('objective:edited', {
        index,
        objective: {
          text: this.levelledObjectiveTexts[index],
          level: this.objectives[index].level,
          title: this.objectives[index].title,
        },
      })
    },
    tabLevelClasses(obj) {
      return {
        'quarter-1-tab': obj.level === ObjectiveLevelValues.QUARTER_1ST,
        'quarter-2-tab': obj.level === ObjectiveLevelValues.QUARTER_2ND,
        'quarter-3-tab': obj.level === ObjectiveLevelValues.QUARTER_3RD,
      }
    },
    clickedTab(index) {
      if (this.objectives.length > 0) {
        this.$sendEvent(
          'Clicked Objective Tab',
          {
            obj_title: this.objectives[index].title,
            level: this.objectives[index].level,
          },
          {
            cu: this.pageUuid,
          }
        )
      }
    },
    loadAllQuarterlyResources() {
      this.resetResourcesMap()
      this.objectives.forEach(({ level }) => {
        this.loadResourcesForQuarter(level)
      })
    },
    resetResourcesMap() {
      this.objectiveResources = emptyResourcesMap()
    },
    loadResourcesForQuarter(quarter) {
      this.loadingResources = true
      getResources(this.resourceUuids[quarter].join(',')).then((data) => {
        data.forEach((resource) => {
          if (resource.type === 'Assessment') {
            this.objectiveResources[quarter].assessments.push(resource)
          } else {
            this.objectiveResources[quarter].resources.push(resource)
          }
        })
        var sortFunc = (a, b) => {
          var indA = this.resourceUuids[quarter].indexOf(a.uuid)
          var indB = this.resourceUuids[quarter].indexOf(b.uuid)
          return indA - indB
        }
        this.objectiveResources[quarter].assessments.sort(sortFunc)
        this.objectiveResources[quarter].resources.sort(sortFunc)

        this.loadingResources = false
      })
    },
    // We use uiv for the tabs and they don't provide a way to target the element with role="tab" through their API,
    // so this is a workaround to set missing aria attributes
    updateAriaSelectedTab() {
      const tabs = this.$el.querySelectorAll('ul[role="tablist"] a[role="tab"]');
      tabs.forEach((tab, index) => {
        tab.setAttribute('aria-selected', this.tabIndex === index ? 'true' : 'false');
      });
    }
  },
  watch: {
    resourceUuids() {
      // preload all resources for SEO; this means we don't need to fetch resources dynamically when tab is clicked
      this.loadAllQuarterlyResources()
    },
    objectiveResourceUuids() {
      this.resetResourcesMap()
    },
    objectives() {
      this.tabIndex = 0
    },
  },
  created() {
    this.loadAllQuarterlyResources()
  },
  updated() {
    this.updateAriaSelectedTab()
  },
}
</script>

<style lang="less" scoped>
@import '~@/variables.less';
.resources-header {
  text-transform: uppercase;
  font-weight: 400;
  margin-top: 10px;
  padding-bottom: 0;
  padding-top: 4px;
  margin-bottom: 0;
}
.resources-container {
  h4:first-child {
    margin-top: 40px;
    padding-top: 8px;
  }
}
/deep/ .quarter-1-tab {
  background-color: @tealObjective !important;
  a {
    background-color: @tealObjective !important;
    .tab-link-style;
  }
  &:not(.active) a {
    &:hover {
      background-color: @tealLightHover !important;
    }
    background-color: @tealLightObjective !important;
  }
  .tabs-style;
}
.quarter-1 {
  .resources-header {
    color: @tealObjective;
    border-top: 1px solid @tealObjective;
  }
}
/deep/ .quarter-2-tab {
  background-color: @blueObjective !important;
  a {
    background-color: @blueObjective !important;
    .tab-link-style;
  }
  &:not(.active) a {
    &:hover {
      background-color: @blueLightHover !important;
    }
    background-color: @blueLightObjective !important;
  }
  .tabs-style;
}
.quarter-2 {
  .resources-header {
    color: @blueObjective;
    border-top: 1px solid @blueObjective;
  }
}
/deep/ .quarter-3-tab {
  background-color: @purpleObjective !important;
  a {
    background-color: @purpleObjective !important;
    .tab-link-style;
  }
  &:not(.active) a {
    &:hover {
      background-color: @purpleLightHover !important;
    }
    background-color: @purpleLight !important;
  }
  .tabs-style;
}
.quarter-3 {
  .resources-header {
    color: @purpleObjective;
    border-top: 1px solid @purpleObjective;
  }
}
.tabs-style {
  font-weight: 600;
  font-size: 18px;
  &:not(.active) {
    font-weight: 400;
  }
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}
.tab-link-style {
  color: white !important;
  border: 0 !important;
  border-top-left-radius: 10px !important;
  border-top-right-radius: 10px !important;
}
.loading-resources {
  margin: 10px auto;
}
</style>
